'use client';

import type { PropsWithChildren } from 'react';

import { IoBuildOutline, IoFileTrayFullOutline } from 'react-icons/io5';

import { useSanitizedHtml } from '../../../utils/sanitize';
import { IconButton } from '../../icon-button';
import {
  CloseButton,
  Resource,
  ResourceBar,
  ResourceBody,
} from '../../resource';
import { AuditLogButton } from '../layout/AuditLogButton';
import { InfoBar } from '../layout/InfoBar';
import { OfficialIcon, TipIcon } from '../layout/InfoIcons';
import { QuestionImage } from '../layout/QuestionImage';
import { TagList } from '../layout/TagList';
import { TimeLimit } from '../layout/TimeLimit';
import type { AssessmentPreviewProps } from './AssessmentPreview.types';

import styles from './AssessmentPreview.module.scss';

function AssessmentPreview({
  style,
  className,
  assessment,
  scoreboard,
  onEdit,
  onViewQuestions,
  onClose,
  testId = 'ui-assessment-preview',
  children,
}: PropsWithChildren<AssessmentPreviewProps>) {
  const descriptionHtml = useSanitizedHtml(assessment.description ?? '');

  return (
    <Resource style={style} className={className} testId={testId}>
      <ResourceBar
        edge="top"
        start={
          <>
            {assessment.official && <OfficialIcon placement="bottom" />}
            {onViewQuestions && (
              <IconButton
                icon={<IoFileTrayFullOutline />}
                aria-label="Ver preguntas"
                onClick={onViewQuestions}
              />
            )}
            {onEdit && (
              <IconButton
                icon={<IoBuildOutline />}
                aria-label="Editar simulacro"
                onClick={onEdit}
              />
            )}
          </>
        }
        center={<TimeLimit seconds={assessment.timeInSeconds} />}
        end={onClose && <CloseButton onClose={onClose} />}
      />
      <ResourceBody className={styles['body']}>
        <div className={styles['head']}>
          <h1 className={styles['name']}>{assessment.name}</h1>
          <span className={styles['caption']}>
            {assessment.numberOfQuestions} preguntas
          </span>
        </div>
        {assessment.description && (
          <div
            className={styles['description']}
            dangerouslySetInnerHTML={descriptionHtml}
          />
        )}
        {assessment.imageUrl && (
          <QuestionImage src={assessment.imageUrl} alt="Imagen del simulacro" />
        )}
        <TagList tags={assessment.tags} />
      </ResourceBody>
      <InfoBar
        difficulty={assessment.difficulty}
        difficultyDetails={scoreboard}
        difficultyDetailsFlush={true}
        options={
          <>
            {assessment.auditLog && assessment.auditLog.length > 0 && (
              <AuditLogButton auditLog={assessment.auditLog} />
            )}
            {assessment.tip && <TipIcon>{assessment.tip}</TipIcon>}
          </>
        }
      >
        {children}
      </InfoBar>
    </Resource>
  );
}

export { AssessmentPreview };
